import { useApolloClient, useMutation } from "@apollo/client"
import { votingIds } from "@artists/challenges-testkit"
import { getDefined } from "@artists/devkit"
import type { VotesToMerge } from "@artists/gql/design-challenges"
import {
	DiscardVotesDocument,
	MergeVotesDocument,
	VotesDocument,
} from "@artists/gql/design-challenges"
import { Confirm } from "@artists/uikit"
import { LoadingButton } from "@mui/lab"
import { Button } from "@mui/material"
import { useCallback } from "react"
import { useTranslation } from "react-i18next"

import { format } from "../../../../utils"

interface MergeVotesPromptProps {
	guestVoting: VotesToMerge
	slug: string
}

export const MergeVotesPrompt = ({
	guestVoting,
	slug,
}: MergeVotesPromptProps) => {
	const { t } = useTranslation("common")
	const { title, primaryButton, secondaryButton } = t("mergeVotes", {
		returnObjects: true,
	})
	const client = useApolloClient()

	const [merge, { loading: mergeLoading, data }] = useMutation(
		MergeVotesDocument,
		{
			variables: { slug },
			notifyOnNetworkStatusChange: true,
			awaitRefetchQueries: true,
		},
	)
	const [discard, { loading: discardLoading }] = useMutation(
		DiscardVotesDocument,
		{
			awaitRefetchQueries: true,
			notifyOnNetworkStatusChange: true,
			refetchQueries: [VotesDocument],
		},
	)

	const onInnerMerge = useCallback(async () => {
		await merge()
		void client.resetStore()
	}, [client, merge])

	const onInnerDiscard = useCallback(() => {
		void discard()
	}, [discard])

	return (
		<Confirm
			open={true}
			showCloseIcon={false}
			title={title}
			subtitle={t("mergeVotes.text", {
				count: getDefined(guestVoting.totalVotes),
				date: format(getDefined(guestVoting.votingDate), "eee, MMM d"),
				time: format(getDefined(guestVoting.votingDate), "h:mma z"),
			})}
			confirmText={primaryButton}
			actions={[
				<LoadingButton
					data-testid={votingIds.mergeVotes}
					color='secondary'
					variant='contained'
					loading={mergeLoading || data?.mergeVotes}
					disabled={
						mergeLoading || discardLoading || data?.mergeVotes
					}
					onClick={onInnerMerge}
					sx={{ textTransform: "none" }}
				>
					{primaryButton}
				</LoadingButton>,
				<Button
					color='secondary'
					variant='contained'
					onClick={onInnerDiscard}
					sx={{ textTransform: "none" }}
				>
					{secondaryButton}
				</Button>,
			]}
		/>
	)
}
